import type { TList } from '@/services/api';
import type { TNetworkError } from '@/services/errors';
import type {
  TEmptyBodyDTO,
  TFactorResetBodyDTO,
  TFactorResetResponseDTO,
  TFactorsResponseDTO,
  TStaffDirectoryDTO,
  TStaffMemberDTO,
  TStaffPermissionDTO,
  TStaffProfileDTO,
  TStaffTransitionReportDTO,
} from './staff.dto';

import { BaseService } from '@/services/api';
import { errorUtils } from '@/services/errors';
import { withSessionHeaders } from '@/services/supabase';

import type {
  TAvailabilityPayload,
  TStaffDirectoryEntry,
  TStaffFactor,
  TStaffMember,
  TStaffPermission,
  TStaffProfile,
  TStaffTransitionPayload,
  TStaffTransitionReport,
} from './staff.types';

import {
  DIRECTORY_FIELDS,
  FACTORS_PATH,
  FACTOR_RESET_PATH,
  PERMISSION_FIELDS,
  PROFILE_STAFF_FIELDS,
  ROSTER_STAFF_FIELDS,
} from './staff.fields';
import {
  toStaffDirectoryEntry,
  toStaffFactor,
  toStaffMember,
  toStaffPermission,
  toStaffProfile,
  toStaffTransitionReport,
} from './staff.adapters';

const STAFF_ROLE = 'staff';

class StaffService extends BaseService {
  protected readonly serviceName = 'staffService';

  public listStaff(): Promise<TList<TStaffMember> | TNetworkError> {
    return this.runList(
      this.db
        .from('roster')
        .select(ROSTER_STAFF_FIELDS, { count: 'exact' })
        .eq('role', STAFF_ROLE)
        .order('last_name', { ascending: true })
        .returns<TStaffMemberDTO[]>(),
      'listStaff',
      toStaffMember,
    );
  }

  public listDirectory(): Promise<TList<TStaffDirectoryEntry> | TNetworkError> {
    return this.runList(
      this.db
        .from('roster')
        .select(DIRECTORY_FIELDS, { count: 'exact' })
        .eq('role', STAFF_ROLE)
        .order('last_name', { ascending: true })
        .returns<TStaffDirectoryDTO[]>(),
      'listDirectory',
      toStaffDirectoryEntry,
    );
  }

  public listPermissions(): Promise<TList<TStaffPermission> | TNetworkError> {
    return this.runList(
      this.db
        .from('staff_permissions')
        .select(PERMISSION_FIELDS, { count: 'exact' })
        .order('action', { ascending: true })
        .returns<TStaffPermissionDTO[]>(),
      'listPermissions',
      toStaffPermission,
    );
  }

  public async listFactors(): Promise<TStaffFactor[] | TNetworkError> {
    const response = await this.post<TFactorsResponseDTO, TEmptyBodyDTO>(
      FACTORS_PATH,
      {},
      { where: 'listFactors', headers: await withSessionHeaders() },
    );

    if (errorUtils.isNetworkError(response)) {
      return response;
    }

    return response.factors.map(toStaffFactor);
  }

  public async resetFactors(staffID: string): Promise<number | TNetworkError> {
    const response = await this.post<TFactorResetResponseDTO, TFactorResetBodyDTO>(
      FACTOR_RESET_PATH,
      { user_id: staffID },
      { where: 'resetFactors', headers: await withSessionHeaders() },
    );

    if (errorUtils.isNetworkError(response)) {
      return response;
    }

    return response.deleted;
  }

  public async setAvailability(payload: TAvailabilityPayload): Promise<string | TNetworkError> {
    const result = await this.run(
      this.db.rpc('set_staff_availability', {
        p_staff_id: payload.staffID,
        p_availability: payload.availability,
        p_away_reason: payload.awayReason ?? undefined,
        p_away_until: payload.awayUntil ?? undefined,
      }),
      'setAvailability',
    );

    if (errorUtils.isNetworkError(result)) {
      return result;
    }

    return result.availability;
  }

  public async transitionStaff(payload: TStaffTransitionPayload): Promise<TStaffTransitionReport | TNetworkError> {
    const result = await this.run(
      this.db.rpc('staff_transition', {
        p_staff_id: payload.staffID,
        p_sub_role: payload.subRole ?? undefined,
        p_availability: payload.availability ?? undefined,
        p_handover: payload.handover,
        p_dry_run: payload.isDryRun,
      }),
      'transitionStaff',
    );

    if (errorUtils.isNetworkError(result)) {
      return result;
    }

    return toStaffTransitionReport(result as TStaffTransitionReportDTO);
  }

  public async readProfile(): Promise<TStaffProfile | TNetworkError> {
    const { data } = await this.db.auth.getSession();
    const userID = data.session?.user.id;

    if (!userID) {
      return errorUtils.createNetworkError('401', undefined, this.callSite('readProfile'));
    }

    return this.run(
      this.db
        .from('roster')
        .select(PROFILE_STAFF_FIELDS)
        .eq('user_id', userID)
        .single()
        .returns<TStaffProfileDTO>(),
      'readProfile',
      toStaffProfile,
    );
  }
}

const staffService = new StaffService();

export {
  staffService,
};
